/**
 * MessageRow Component
 *
 * Displays a message (WhatsApp/Telegram/SMS) in row format using BaseRow layout.
 * Shows platform, contact, preview, and timestamp.
 */

import React, { useMemo, useState } from 'react';
import { MessageCircle } from 'lucide-react';
import { BaseRow } from '../common/base/BaseRow';
import { PrimaryAction, EntityAvatar } from '../common/base';
import { LinkManager } from '../../services/actions/LinkManager';
import { getAvailableActions } from '../common/registry/ActionRegistry';
import { useTranslation } from '../../hooks';

// =============================================================================
// TYPES
// =============================================================================

export type MessagePlatform = 'whatsapp' | 'telegram' | 'sms' | 'other';

export interface Message {
  id: string; // msg-xxx
  platform: MessagePlatform;
  contactId?: string; // cnt-xxx
  contactName?: string;
  text: string;
  direction: 'in' | 'out';
  at: string; // ISO date
  read?: boolean;
}

export interface MessageRowProps {
  entity: Message;
  onTitleClick?: (message: Message) => void;
  onActionClick?: (actionType: string, messageId: string) => void;
  onAvatarClick?: (name: string, id?: string) => void;
}

// =============================================================================
// COMPONENT
// =============================================================================

export function MessageRow({ entity: message, onTitleClick, onActionClick, onAvatarClick }: MessageRowProps) {
  const { t, language } = useTranslation();
  const locale = language === 'it' ? 'it-IT' : 'en-US';

  // Locale-aware relative time formatting
  const formatTime = (isoDate: string): string => {
    const date = new Date(isoDate);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return t('common.now');
    if (diffMins < 60) return t('common.minutesAgo', { count: diffMins });
    if (diffHours < 24) return t('common.hoursAgo', { count: diffHours });
    if (diffDays < 7) return t('common.daysAgo', { count: diffDays });

    return date.toLocaleDateString(locale, {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Primary action state (archived)
  const [isArchived, setIsArchived] = useState(false);

  // Get linked action counts for icon coloring
  const linkedCounts = useMemo(() => {
    return LinkManager.getLinkedActionCounts(message.id);
  }, [message.id]);

  // Left slot - Primary Action + Contact Avatar
  const leftSlot = (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <PrimaryAction
        type="archive"
        active={isArchived}
        onChange={(archived) => {
          setIsArchived(archived);
          onActionClick?.('archive', message.id);
        }}
      />
      <EntityAvatar
        items={message.contactName ? { id: message.contactId, name: message.contactName } : []}
        type="person"
        size={24}
        onClick={onAvatarClick ? (item) => onAvatarClick(item.name, item.id) : undefined}
      />
    </div>
  );

  // Get available actions for messages
  const availableActions = useMemo(() => {
    return getAvailableActions(message.id);
  }, [message.id]);

  // Build title (contact name or preview)
  const title = useMemo(() => {
    if (message.contactName) {
      return message.contactName;
    }
    // Return first 50 chars of message as title
    return message.text.length > 50
      ? message.text.substring(0, 50) + '...'
      : message.text;
  }, [message]);

  // Build meta content
  const metaContent = useMemo(() => {
    const parts: React.ReactNode[] = [];

    // Platform badge
    parts.push(
      <span key="platform" style={getPlatformStyle(message.platform)}>
        {t(`messages.${message.platform}`)}
      </span>
    );

    // Message preview (if we have contact name in title)
    if (message.contactName) {
      const preview = message.text.length > 40
        ? message.text.substring(0, 40) + '...'
        : message.text;
      parts.push(
        <span key="preview" style={styles.preview}>
          {preview}
        </span>
      );
    }

    // Timestamp
    parts.push(
      <span key="time" style={styles.time}>
        {formatTime(message.at)}
      </span>
    );

    return (
      <div style={styles.metaContainer}>
        {parts.map((part, idx) => (
          <React.Fragment key={idx}>
            {idx > 0 && <span style={styles.separator}>·</span>}
            {part}
          </React.Fragment>
        ))}
      </div>
    );
  }, [message, t, formatTime]);

  // Icon with platform color
  const icon = (
    <MessageCircle
      size={16}
      style={{ color: getPlatformColor(message.platform) }}
    />
  );

  return (
    <BaseRow
      entity={{ ...message, title }}
      entityType="message"
      leftSlot={leftSlot}
      icon={icon}
      metaContent={metaContent}
      availableActions={availableActions}
      linkedActionCounts={linkedCounts}
      onTitleClick={onTitleClick}
      onActionClick={onActionClick}
      done={isArchived || message.read}
    />
  );
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

// getPlatformLabel is now handled by translation t(`messages.${platform}`)

function getPlatformColor(platform: MessagePlatform): string {
  const colors: Record<MessagePlatform, string> = {
    whatsapp: '#25D366',
    telegram: '#0088cc',
    sms: '#3b82f6',
    other: '#9ca3af',
  };
  return colors[platform];
}

function getPlatformStyle(platform: MessagePlatform): React.CSSProperties {
  const baseStyle = {
    padding: '2px 6px',
    borderRadius: '3px',
    fontSize: '11px',
    fontWeight: 500,
  };

  const platformStyles: Record<MessagePlatform, React.CSSProperties> = {
    whatsapp: {
      ...baseStyle,
      background: '#d1f4dd',
      color: '#0a5c2e',
    },
    telegram: {
      ...baseStyle,
      background: '#d6ebf5',
      color: '#005580',
    },
    sms: {
      ...baseStyle,
      background: '#dbeafe',
      color: '#1e40af',
    },
    other: {
      ...baseStyle,
      background: '#f3f4f6',
      color: '#6b7280',
    },
  };

  return platformStyles[platform];
}

// formatTime is now handled inside the component with locale awareness

// =============================================================================
// STYLES
// =============================================================================

const styles = {
  metaContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '12px',
  } as React.CSSProperties,
  separator: {
    color: '#ccc',
    margin: '0 2px',
  } as React.CSSProperties,
  preview: {
    color: '#666',
    fontStyle: 'italic',
  } as React.CSSProperties,
  time: {
    color: '#999',
  } as React.CSSProperties,
};
